// The frame every profile block sits in: the heading, the link beside it, the
// editing tools, and the drag handlers.
//
// The frame is the same for all of them so a block only has to draw its own
// contents, and so the page reads as one page rather than ten panels that each
// invented a header.

import type { CSSProperties, DragEvent, ReactNode } from 'react';
import { columnSpan } from './types.ts';
import type { Widget } from './types.ts';

export interface ShellTools {
  move: (by: number) => void;
  width: () => void;
  box: () => void;
  corner: () => void;
  texture: () => void;
  opacity: () => void;
  hide: () => void;
}

export interface ShellDrag {
  active: boolean;
  over: boolean;
  onStart: (event: DragEvent<HTMLElement>) => void;
  onOver: (event: DragEvent<HTMLElement>) => void;
  onLeave: () => void;
  onDrop: (event: DragEvent<HTMLElement>) => void;
  onEnd: () => void;
}

function frame(widget: Widget): CSSProperties {
  const radius =
    widget.corner === 'sharp'
      ? 0
      : widget.corner === 'round'
        ? 22
        : widget.corner === 'circle'
          ? '50%'
          : 10;
  return {
    gridColumn: `span ${columnSpan(widget.w)}`,
    borderRadius: radius,
    opacity: widget.opacity / 100,
    ...(widget.corner === 'circle'
      ? { aspectRatio: '1', display: 'grid', placeContent: 'center' }
      : null),
  };
}

export function WidgetShell({
  widget,
  editing,
  title,
  aside,
  tools,
  drag,
  onRename,
  children,
}: {
  widget: Widget;
  editing: boolean;
  title: string;
  aside?: ReactNode;
  tools: ShellTools;
  drag: ShellDrag;
  onRename: (title: string) => void;
  children: ReactNode;
}) {
  return (
    <section
      className={[
        'widget',
        `box-${widget.style}`,
        `tex-${widget.texture}`,
        editing ? 'draggable' : '',
        drag.over ? 'dropping' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={frame(widget)}
      draggable={editing}
      onDragStart={drag.onStart}
      onDragOver={drag.onOver}
      onDragLeave={drag.onLeave}
      onDrop={drag.onDrop}
      onDragEnd={drag.onEnd}
    >
      {editing && (
        <div className="widgettools" aria-label={`Arrange ${title}`}>
          <button onClick={() => tools.move(-1)} title="Move earlier">
            ←
          </button>
          <button onClick={() => tools.move(1)} title="Move later">
            →
          </button>
          <button onClick={tools.width} title="Width">
            {widget.w}%
          </button>
          <button onClick={tools.box} title="Box">
            {widget.style}
          </button>
          <button onClick={tools.corner} title="Corners">
            {widget.corner}
          </button>
          <button onClick={tools.texture} title="Texture">
            {widget.texture}
          </button>
          <button onClick={tools.opacity} title="Opacity">
            {widget.opacity}%
          </button>
          <button onClick={tools.hide} title="Hide">
            hide
          </button>
        </div>
      )}

      <div className="widgethead">
        {editing ? (
          <h2
            className="editable"
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) => onRename(e.currentTarget.textContent?.trim() ?? '')}
          >
            {title}
          </h2>
        ) : (
          <h2>{title}</h2>
        )}
        {aside}
      </div>

      <div className="widgetbody">{children}</div>
    </section>
  );
}
